import { detectCapabilities } from './capabilities';

/**
 * Focus handling between the task pane and the document.
 *
 * WINDOWS-ONLY BUG (annotation inserted a second time): `Range.select()` moves
 * the Word CARET but not keyboard focus. On Windows the pane's WebView2 keeps
 * DOM focus after a click, so the button the user just pressed stays focused —
 * and the next Space, meant to be typed into the annotation, activates that
 * button again (Space/Enter activate a focused HTML button) and inserts the
 * annotation twice. Word for Mac hands focus to the document on `select()`,
 * which is why this never showed there.
 *
 * Two layers, because the first is guaranteed and the second is best-effort:
 *  - `releasePaneFocus()` — always available, kills the double insert.
 *  - `focusDocumentWindow()` — additionally puts focus where the user expects
 *    it (the caret in the document), where the host supports it.
 */

/** Drop DOM focus inside the pane, so Space/Enter can't re-activate a button. */
export function releasePaneFocus(): void {
  const active = typeof document !== 'undefined' ? document.activeElement : null;
  if (active instanceof HTMLElement) active.blur();
}

/**
 * Hand keyboard focus back to the document window so the user can type at the
 * caret the action just placed (e.g. inside the `[V14: |.]` template).
 *
 * Needs `Window.setFocus` (WordApiDesktop 1.4 — desktop only); on hosts without
 * it the pane blur alone prevents the double insert and the user clicks into the
 * document as before. Runs in its OWN batch: a failure here must never roll back
 * the insert that just succeeded.
 */
export async function focusDocumentWindow(): Promise<void> {
  releasePaneFocus();
  if (!detectCapabilities().documentFocus) return;
  try {
    await Word.run(async (context) => {
      context.document.activeWindow.setFocus();
      await context.sync();
    });
  } catch (e) {
    console.warn('[focus] could not return focus to the document:', e);
  }
}
